/**
 * Initialize checklist toggle functionality
 * Allows users to click items to mark them as complete
 *
 * @since 1.0.0
 */

/**
 * Selector matching interactive descendants that must never be nested inside a
 * <button>. The HTML spec forbids interactive content (links, form controls,
 * other buttons, etc.) inside a <button>, and doing so also breaks their own
 * activation. List items containing any of these are left as plain items.
 */
const interactiveContentSelector =
	'a[href], button, input, select, textarea, label, details, [tabindex], [contenteditable="true"]';

/**
 * Initialize checklist functionality for all checklist items
 */
export function initChecklist() {
	const checklists = document.querySelectorAll('.wp-block-list.is-style-checklist');

	checklists.forEach((checklist) => {
		const items = checklist.querySelectorAll('li');

		items.forEach((item, index) => {
			// Skip items that already contain interactive content. Wrapping such
			// content in a <button> would produce invalid HTML and stop the
			// nested control (e.g. a link) from working, so these items keep
			// their default list-item rendering with no toggle behaviour.
			if (item.querySelector(interactiveContentSelector)) {
				return;
			}

			// Create a unique key for localStorage based on page pathname and item index
			const storageKey = `checklist-${window.location.pathname}#${index}`;

			// The interactive checkbox behaviour must live on a child element,
			// not on the <li> itself. Putting role="checkbox" (or any non-list
			// role) directly on an <li> strips its implicit "listitem" role,
			// which makes assistive tech and accessibility scanners treat the
			// parent <ol>/<ul> as containing invalid children. To stay WCAG
			// compliant, the <li> is kept as a plain list item and all of its
			// content is wrapped in a real <button> that carries the checkbox
			// semantics.
			const toggle = document.createElement('button');
			toggle.type = 'button';
			toggle.className = 'checklist-toggle';
			toggle.setAttribute('role', 'checkbox');
			const content = document.createElement('span');
			content.className = 'checklist-content';

			// Move the list item's existing content inside the button so the
			// whole item remains clickable and readable by screen readers.
			while (item.firstChild) {
				content.appendChild(item.firstChild);
			}
			toggle.appendChild(content);
			item.appendChild(toggle);

			// Restore checked state from localStorage
			const isChecked = localStorage.getItem(storageKey) === 'checked';
			if (isChecked) {
				toggle.classList.add('is-checked');
			}
			toggle.setAttribute('aria-checked', isChecked);

			// A native <button> already fires "click" for pointer input and for
			// keyboard activation (Enter and Space), so a single listener covers
			// both interaction methods and no manual keydown handling is needed.
			toggle.addEventListener('click', () => {
				toggleChecked(toggle, storageKey);
			});
		});
	});
}

/**
 * Toggle checked state and persist to localStorage
 *
 * @param {Element} toggle - The checkbox button to toggle
 * @param {string} storageKey - The localStorage key
 */
function toggleChecked(toggle, storageKey) {
	toggle.classList.toggle('is-checked');
	const isChecked = toggle.classList.contains('is-checked');

	// Update aria-checked attribute
	toggle.setAttribute('aria-checked', isChecked);

	// Persist state to localStorage
	if (isChecked) {
		localStorage.setItem(storageKey, 'checked');
	} else {
		localStorage.removeItem(storageKey);
	}
}

